/**
 * Gemeinsame Seite aller fuenf Marktauswertungen nach Sketch 023 Variante C, Runde 7 (D-01):
 * Kopf, Periodenleiste, Statusleiste "Pruefung und Freigabe", Tabellenkarte mit Legende, Leerfall
 * und Hervorhebung ueber "zeigen" (D-19). Jahr/Periode leben ausschliesslich in der URL -- kein
 * doppelter lokaler Zustand dafuer. Diese Seite berechnet selbst nichts: alle Werte, Zaehler und
 * Zustaende kommen fertig aus `useEvaluationTable` (39-08/39-10).
 */
import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router';
import { Alert, Button, Card, Select, Stack, Text } from '@mantine/core';
import { useWindowEvent } from '@mantine/hooks';
import { EvaluationBreakdownModal } from '../../components/evaluations/EvaluationBreakdownModal';
import { PeriodStrip } from '../../components/evaluations/PeriodStrip';
import { RegionalEvaluationTable } from '../../components/evaluations/RegionalEvaluationTable';
import { ReleaseStatusBar } from '../../components/evaluations/ReleaseStatusBar';
import { SkeletonTable } from '../../components/evaluations/SkeletonTable';
import { TransferLinkRow } from '../../components/evaluations/TransferLinkRow';
import { ZementartenMatrix } from '../../components/evaluations/ZementartenMatrix';
import { PageHeader } from '../../components/ui/PageHeader';
import { useEvaluationTable } from '../../hooks/useEvaluationTable';
import {
  EvaluationTypeLabels,
  type EvaluationType,
} from '../../types/evaluations';

/** Untertitel je Auswertung (Sketch EVALS.desc), siehe 39-13-PLAN.md <interfaces>. */
const SUBTITLES: Record<EvaluationType, string> = {
  inlandsversand:
    'Versand ins Inland nach Monaten, getrennt nach alten und neuen Bundesländern (Lieferziel).',
  versandstatistik: 'Versand nach Regionen des Werksstandorts, je Quartal.',
  gebietsaufnahme: 'Versand nach Bundesland des Lieferziels, je Quartal.',
  zementverwendung: 'Anteil der Verwendungsarten am Inlandsversand, je Jahr.',
  zementarten:
    'Inlandsversand nach Zementarten und Festigkeitsklassen, je Jahr.',
};

export interface MarketEvaluationPageProps {
  evaluationType: EvaluationType;
}

export function MarketEvaluationPage({
  evaluationType,
}: MarketEvaluationPageProps) {
  const [searchParams, setSearchParams] = useSearchParams();

  const yearParam = searchParams.get('year');
  const periodParam = searchParams.get('period');
  const requestedYear = yearParam !== null ? Number(yearParam) : undefined;
  const requestedPeriod =
    periodParam !== null ? Number(periodParam) : undefined;

  const { data, loading, error, refresh } = useEvaluationTable(
    evaluationType,
    requestedYear,
    requestedPeriod
  );

  const [openCellKey, setOpenCellKey] = useState<string | null>(null);
  const [highlight, setHighlight] = useState<ReadonlySet<string>>(new Set());
  const [breakdownRowKey, setBreakdownRowKey] = useState<string | null>(null);
  // D-09: erhoeht sich nach jeder Uebertragung und jeder Sperrentscheidung -- beides aendert den
  // Uebertragungsstand, den die Link-Zeile zeigt. `refresh()` allein reicht nicht: die Tabelle laedt
  // dieselbe URL erneut, die Link-Zeile braucht ein eigenes Signal fuer ihren useEffect.
  const [transferRefreshKey, setTransferRefreshKey] = useState(0);

  // D-02/D-09: fehlt year/period in der URL, waehlt der Server selbst den letzten Zeitraum mit
  // Daten -- die tatsaechlich gezeigte Antwort wird danach in die URL geschrieben (kein
  // zusaetzlicher History-Eintrag). Nur eine Antwort DIESER Auswertung: beim Wechsel liegt kurz
  // noch die vorige vor (Inlandsversand period=8 waere fuer die Versandstatistik ungueltig).
  useEffect(() => {
    if (
      data &&
      data.evaluationType === evaluationType &&
      (yearParam === null || periodParam === null)
    ) {
      setSearchParams(
        { year: String(data.year), period: String(data.period) },
        { replace: true }
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, evaluationType, yearParam, periodParam]);

  function handleChanged() {
    refresh();
    setTransferRefreshKey((key) => key + 1);
  }

  function selectPeriod(year: number, period: number) {
    setOpenCellKey(null);
    setHighlight(new Set());
    setSearchParams({ year: String(year), period: String(period) });
  }

  useWindowEvent('keydown', (event) => {
    if (event.key !== 'Escape') {
      return;
    }
    setHighlight(new Set());
    setOpenCellKey(null);
  });

  // Aus dem Klick-Menue heraus: erst das Menue schliessen, sonst liegt es ueber dem Dialog.
  function openBreakdown(rowKey: string) {
    setOpenCellKey(null);
    setBreakdownRowKey(rowKey);
  }

  function handleHighlight(cellKeys: string[], anchorCellKey: string) {
    setHighlight(new Set(cellKeys));
    document
      .querySelector(`[data-cell-key="${anchorCellKey}"]`)
      ?.scrollIntoView({ block: 'center' });
  }

  const showYearSelect =
    data !== null &&
    (data.interval === 'monthly' || data.interval === 'quarterly');

  return (
    <Stack gap="lg">
      <PageHeader
        title={data?.title ?? EvaluationTypeLabels[evaluationType]}
        breadcrumb={{ label: 'Zurück zur Übersicht', to: '/evaluations' }}
        description={SUBTITLES[evaluationType]}
        rightSection={
          showYearSelect && data ? (
            <Select
              label="Jahr"
              w={110}
              data={data.availableYears.map((year) => String(year))}
              value={String(data.year)}
              allowDeselect={false}
              onChange={(value) => {
                if (value) {
                  selectPeriod(Number(value), data.period);
                }
              }}
            />
          ) : undefined
        }
      />

      {error && (
        <Alert color="red" title="Fehler">
          {error}
        </Alert>
      )}

      {loading && <SkeletonTable rows={13} columns={4} />}

      {!loading && data && (
        <>
          <PeriodStrip
            entries={data.strip}
            selected={{ year: data.year, period: data.period }}
            onSelect={selectPeriod}
          />

          {data.importLocked && (
            <Alert color="gray" title="Importierter Zeitraum">
              Die Werte stammen aus dem Statistik-Portal und sind endgültig. Es
              gibt keine Aufschlüsselung je Werk und keine Sperrentscheidungen.
            </Alert>
          )}

          {!data.hasData ? (
            <Card withBorder radius="md" p="lg">
              <Text fw={700}>
                Für {data.periodLabel} liegen noch keine Meldungen vor.
              </Text>
              <Text size="sm" c="dimmed" mt={4}>
                Die Auswertung entsteht, sobald die ersten Werke ihre Meldung
                für diesen Zeitraum speichern.
              </Text>
              {data.lastPeriodWithData && (
                <Button
                  mt="md"
                  variant="light"
                  onClick={() => {
                    if (data.lastPeriodWithData) {
                      selectPeriod(
                        data.lastPeriodWithData.year,
                        data.lastPeriodWithData.period
                      );
                    }
                  }}
                >
                  Zum letzten Zeitraum mit Daten:{' '}
                  {data.lastPeriodWithData.label}
                </Button>
              )}
            </Card>
          ) : (
            <>
              <ReleaseStatusBar
                evaluationType={evaluationType}
                year={data.year}
                period={data.period}
                title={data.title}
                reported={data.reported}
                locks={data.statusBar}
                onChanged={handleChanged}
                onHighlight={handleHighlight}
                footer={
                  <TransferLinkRow
                    evaluationType={evaluationType}
                    year={data.year}
                    period={data.period}
                    refreshKey={transferRefreshKey}
                  />
                }
              />

              <div>
                {data.table?.layout === 'zementarten' ? (
                  <ZementartenMatrix
                    data={data}
                    openCellKey={openCellKey}
                    onOpenCell={setOpenCellKey}
                    onCloseCell={() => setOpenCellKey(null)}
                    highlight={highlight}
                    onChanged={refresh}
                    onOpenBreakdown={openBreakdown}
                  />
                ) : (
                  <RegionalEvaluationTable
                    data={data}
                    openCellKey={openCellKey}
                    onOpenCell={setOpenCellKey}
                    onCloseCell={() => setOpenCellKey(null)}
                    highlight={highlight}
                    onChanged={refresh}
                    onOpenBreakdown={openBreakdown}
                  />
                )}
              </div>
            </>
          )}
        </>
      )}

      <EvaluationBreakdownModal
        opened={breakdownRowKey !== null}
        onClose={() => setBreakdownRowKey(null)}
        evaluationType={evaluationType}
        year={data?.year ?? 0}
        period={data?.period ?? 0}
        rowKey={breakdownRowKey}
      />
    </Stack>
  );
}
